import { useEffect, useRef, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import { useStrategy } from "../api/strategy";
import { useAutoRecalculate } from "../hooks/useAutoRecalculate";

type Severity = "warning" | "error";

// Surfaces fuel-load calculation state to the user and reports failures. With
// the auto-update preference off, prompts a recalculation when inputs change;
// the Recalculate button in StrategiesBar is the action/retry. With it on,
// edits recalculate automatically so the prompt is suppressed.
export default function FuelLoadStatusSnackbar() {
  const { isOutOfDate, status } = useStrategy();
  const autoRecalculate = useAutoRecalculate();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: Severity;
  }>({ open: false, message: "", severity: "warning" });

  // Fire once per transition rather than on every render.
  const prevOutOfDate = useRef(isOutOfDate);
  const prevState = useRef(status?.state);

  useEffect(() => {
    if (!autoRecalculate && isOutOfDate && !prevOutOfDate.current) {
      setSnackbar({
        open: true,
        message: "Fuel loads are out of date — click Recalculate to update them.",
        severity: "warning",
      });
    }
    prevOutOfDate.current = isOutOfDate;
  }, [isOutOfDate, autoRecalculate]);

  useEffect(() => {
    const state = status?.state;
    if (state === "failed" && prevState.current !== "failed") {
      setSnackbar({
        open: true,
        message: "There was an error calculating fuel loads. Please try again.",
        severity: "error",
      });
    }
    prevState.current = state;
  }, [status?.state]);

  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      // Sit above the fixed FooterStatusBar (height 24).
      sx={{ bottom: { xs: 40, sm: 40 } }}
    >
      <Alert
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        sx={{ width: "100%" }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
}
